/**
 * Admin Panel Component
 * Управление пользователями (только для super_admin)
 */

import React, { useState, useEffect } from "react";
import { Section, ToolbarButton } from "./UI";
import { adminService } from "../services/adminService";
import { useToast } from "./Toast";

export function AdminPanel({ setView }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({
    telegram_id: "",
    role: "manager",
  });
  const toast = useToast();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const usersData = await adminService.getUsers();
      setUsers(usersData);
    } catch (err) {
      console.error("Failed to load users:", err);
      setError(err.message || "Ошибка загрузки пользователей");
      toast.error("Ошибка загрузки пользователей");
    } finally {
      setLoading(false);
    }
  };

  const handlePromote = async (telegramId, newRole) => {
    try {
      await adminService.promoteUser(telegramId, newRole);
      toast.success(`Пользователь успешно назначен как ${newRole === "admin" ? "Админ" : "Менеджер"}`);
      await loadUsers();
    } catch (err) {
      console.error("Failed to promote user:", err);
      toast.error(`Ошибка изменения роли: ${err.message}`);
    }
  };

  const handleDemote = async (telegramId) => {
    if (!confirm("Понизить пользователя до менеджера?")) return;

    try {
      await adminService.demoteUser(telegramId);
      toast.success("Пользователь понижен до менеджера");
      await loadUsers();
    } catch (err) {
      console.error("Failed to demote user:", err);
      toast.error(`Ошибка понижения: ${err.message}`);
    }
  };

  const handleDeleteUser = async (telegramId, userName) => {
    if (!confirm(`Удалить пользователя ${userName}?\n\nПользователь потеряет доступ к приложению.`)) return;

    try {
      await adminService.deleteUser(telegramId);
      toast.success("Пользователь удалён");
      await loadUsers();
    } catch (err) {
      console.error("Failed to delete user:", err);
      toast.error(`Ошибка удаления: ${err.message}`);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();

    if (!newUser.telegram_id || isNaN(newUser.telegram_id)) {
      toast.error("Введите корректный Telegram ID");
      return;
    }

    try {
      await adminService.createUser(parseInt(newUser.telegram_id), newUser.role);
      toast.success(`Пользователь добавлен в систему с ролью ${newUser.role === "admin" ? "Админ" : "Менеджер"}`);
      setNewUser({ telegram_id: "", role: "manager" });
      setShowAddForm(false);
      await loadUsers();
    } catch (err) {
      console.error("Failed to add user:", err);
      toast.error(`Ошибка добавления: ${err.message}`);
    }
  };

  if (loading && users.length === 0) {
    return (
      <Section title="👥 Управление пользователями" right={<ToolbarButton onClick={() => setView("home")}>Назад</ToolbarButton>}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Загрузка пользователей...</p>
          </div>
        </div>
      </Section>
    );
  }

  if (error && users.length === 0) {
    return (
      <Section title="👥 Управление пользователями" right={<ToolbarButton onClick={() => setView("home")}>Назад</ToolbarButton>}>
        <div className="text-center py-8">
          <p className="text-red-500 mb-4">❌ {error}</p>
          <ToolbarButton onClick={loadUsers}>Попробовать снова</ToolbarButton>
        </div>
      </Section>
    );
  }

  return (
    <Section 
      title="👥 Управление пользователями"
      right={
        <div className="flex gap-2">
          <ToolbarButton onClick={() => setShowAddForm(!showAddForm)} active={showAddForm}>
            {showAddForm ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              "+"
            )}
          </ToolbarButton>
          <ToolbarButton onClick={loadUsers}>🔄</ToolbarButton>
          <ToolbarButton onClick={() => setView("home")}>Назад</ToolbarButton>
        </div>
      }
    >
      {/* Форма добавления пользователя */}
      {showAddForm && (
        <form onSubmit={handleAddUser} className="mb-6 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900">
          <h3 className="font-semibold mb-3">Добавить пользователя</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Telegram ID</label>
              <input
                type="text"
                value={newUser.telegram_id}
                onChange={(e) => setNewUser({ ...newUser, telegram_id: e.target.value })}
                placeholder="123456789"
                className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 outline-none transition-colors"
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Числовой ID пользователя в Telegram
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Роль</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 outline-none transition-colors"
              >
                <option value="manager">Менеджер</option>
                <option value="admin">Администратор</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <ToolbarButton active type="submit">Добавить</ToolbarButton>
            <ToolbarButton type="button" onClick={() => setShowAddForm(false)}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </ToolbarButton>
          </div>
        </form>
      )}

      {/* Список пользователей */}
      <div className="space-y-2">
        {users.map((user) => {
          const roleColors = {
            super_admin: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200",
            admin: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
            manager: "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200",
          };

          const roleLabels = {
            super_admin: "Супер Админ",
            admin: "Администратор",
            manager: "Менеджер",
          };

          return (
            <div 
              key={user.telegram_id}
              className="relative p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {/* Pill-статус в правом верхнем углу */}
              <div className="absolute top-2 right-2">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${roleColors[user.role]}`}>
                  {roleLabels[user.role]}
                </span>
              </div>

              {/* Основная информация */}
              <div className={`pr-20 ${user.role !== "super_admin" ? "pb-7" : ""}`}>
                <div className="font-medium text-base mb-0.5">
                  {user.first_name} {user.last_name ? user.last_name.charAt(0) + '.' : ''}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {user.username ? `@${user.username}` : `ID: ${user.telegram_id}`}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Лидов: {user.leads_count}
                </div>
              </div>

              {/* Кнопки управления в правом нижнем углу (только если не super_admin) */}
              {user.role !== "super_admin" && (
                <div className="absolute bottom-2 right-2 flex gap-1.5">
                  {user.role === "manager" && (
                    <button
                      onClick={() => handlePromote(user.telegram_id, "admin")}
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-900 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm"
                      title="Повысить до администратора"
                    >
                      ↑
                    </button>
                  )}
                  {user.role === "admin" && (
                    <button
                      onClick={() => handleDemote(user.telegram_id)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-yellow-100 dark:hover:bg-yellow-900 text-gray-700 dark:text-gray-300 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors text-sm"
                      title="Понизить до менеджера"
                    >
                      ↓
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteUser(user.telegram_id, user.first_name || user.username || `ID ${user.telegram_id}`)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900 text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-colors text-xs"
                    title="Удалить пользователя"
                  >
                    🗑️
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {users.length === 0 && (
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">Нет пользователей</p>
      )}

      {/* Итоговая статистика */}
      <div className="mt-6 p-4 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-3 gap-6 text-center">
          <div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {users.filter(u => u.role === "super_admin").length}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Супер Админов</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {users.filter(u => u.role === "admin").length}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Администраторов</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {users.filter(u => u.role === "manager").length}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Менеджеров</div>
          </div>
        </div>
      </div>
    </Section>
  );
}
